import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import JsonLd, { getOrganizationSchema, getWebPageSchema, getBreadcrumbSchema, getFAQSchema } from '@/components/feature/JsonLd';
import { siteStats } from '@/mocks/home';
import ContactForm from './components/ContactForm';

export default function AboutPage() {
  const faqs = [
    { question: '爱漫画网是否真的免费？', answer: '是的，爱漫画网上的所有漫画资源均免费提供在线阅读，无需付费订阅。我们通过广告以外的其他方式维持平台运营，确保读者享受免费看漫画全集的体验。' },
    { question: '什么是下拉式漫画阅读？', answer: '下拉式漫画阅读是一种创新的阅读模式，用户只需向下滑动屏幕即可连续阅读多话漫画，无需频繁点击"下一话"按钮。这种模式让阅读体验更加流畅，特别适合追更长篇漫画。' },
    { question: '支持哪些阅读模式？', answer: '爱漫画网目前支持四种阅读模式：下拉式漫画阅读（连续滚动）、翻页漫画（模拟纸质书翻页）、无弹窗漫画（纯净阅读环境）和漫画全屏观看（沉浸式体验）。你可以根据个人喜好自由切换。' },
    { question: '漫画资源多久更新一次？', answer: '我们每日都会更新漫画资源，确保读者能第一时间看到最新话数。热门作品的更新速度通常与官方同步，让你不会错过任何精彩内容。' },
    { question: '如何在手机上获得最佳阅读体验？', answer: '推荐使用漫画全屏观看模式，隐藏浏览器的导航栏和状态栏，让屏幕充分利用。对于条漫类作品，下拉式漫画阅读最为舒适；对于页漫类作品，翻页漫画更能还原纸质书的阅读感。' },
  ];

  return (
    <>
      <JsonLd data={getOrganizationSchema()} />
      <JsonLd data={getWebPageSchema(
        '关于爱漫画网 - 品牌介绍、口碑与联系方式',
        '了解爱漫画网的品牌故事、800万+用户的口碑评价、常见问题解答及联系方式。致力于提供下拉式漫画阅读、翻页漫画、无弹窗漫画等优质阅读体验。',
        'https://aimanhua.com/about'
      )} />
      <JsonLd data={getBreadcrumbSchema([
        { name: '首页', url: 'https://aimanhua.com' },
        { name: '关于我们', url: 'https://aimanhua.com/about' },
      ])} />
      <JsonLd data={getFAQSchema(faqs)} />
      <Layout>
        <div className="pt-16">
          {/* Breadcrumb */}
          <div className="bg-background-50 border-b border-background-200/60">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-800 font-medium">关于我们</span>
              </nav>
            </div>
          </div>

          {/* Hero */}
          <section className="bg-background-50 border-b border-background-200/60">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-16 md:py-20 text-center">
              <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-4">关于爱漫画网</h1>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-sm md:text-base">
                爱漫画网致力于为广大漫画爱好者提供最优质的在线阅读体验。我们深知漫迷的需求，不断创新阅读模式，
                让每一位用户都能找到最适合自己的追漫方式。
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-5">品牌故事</h2>
                  <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                    <p>
                      爱漫画网成立于对漫画阅读体验的深刻洞察之上。我们的创始团队发现，市面上的漫画平台充斥着弹窗广告
                      和糟糕的阅读体验——频繁的翻页、烦人的弹窗、不流畅的加载速度，让真正热爱漫画的人苦不堪言。
                    </p>
                    <p>
                      于是，我们决定打造一个真正属于漫迷的阅读平台。<strong>下拉式漫画阅读</strong>让读者可以像刷短视频一样
                      流畅追漫，<strong>翻页漫画</strong>还原了纸质书的阅读仪式感，<strong>漫画全屏观看</strong>则让读者完全沉浸在
                      漫画世界中。最重要的是，我们承诺在所有阅读模式下都坚持<strong>无弹窗漫画</strong>体验。
                    </p>
                    <p>
                      如今，爱漫画网已经成长为拥有<strong>800万+忠实用户</strong>的漫画阅读平台，收录了<strong>5000+部</strong>
                      优质漫画资源，涵盖了热血、恋爱、奇幻、搞笑、科幻、悬疑、武侠等几乎所有主流类型。
                    </p>
                    <p>
                      每一位漫迷的声音都推动着我们不断进步。我们将持续优化阅读体验，丰富漫画资源库，让<strong>免费看漫画全集</strong>
                      变得更加轻松愉快。
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20cozy%20reading%20environment%20with%20digital%20tablet%20displaying%20colorful%20comic%20pages%2C%20warm%20ambient%20lighting%2C%20bookshelf%20background%2C%20comfortable%20chair%2C%20clean%20minimalist%20aesthetic%2C%20lifestyle%20photography&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="爱漫画网品牌形象"
                    title="爱漫画网 - 优质漫画阅读体验"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-14 bg-primary-500">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {siteStats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl md:text-4xl font-heading text-white mb-2">{stat.value}</div>
                    <div className="text-sm text-white/80">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-14 md:py-20">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-10">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { name: '追漫达人小王', text: '用了爱漫画网之后再也回不去了！下拉式漫画阅读真的太方便了，一口气能看好几十话，完全停不下来。无弹窗的体验也很赞！', rating: 5 },
                  { name: '二次元老司机', text: '翻页漫画这个功能太贴心了，看《镖人》这种画风精致的作品，翻页的感觉就像在看实体书。全屏观看更是沉浸感满满。', rating: 5 },
                  { name: '深夜追漫人', text: '最爱的就是无弹窗漫画这个承诺。以前在其他平台看漫画，弹窗广告真的烦死了。爱漫画网真的做到了纯净阅读，给一万个好评！', rating: 5 },
                ].map((review, i) => (
                  <div key={i} className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">{review.text}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary-600">{review.name.charAt(0)}</span>
                      </div>
                      <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-10">常见问题</h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <details key={i} className="bg-background-50 rounded-xl border border-background-200/60 group">
                    <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-14 md:py-20">
            <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-4">联系我们</h2>
              <p className="text-sm text-foreground-500 text-center mb-10 max-w-lg mx-auto">
                如果你有任何建议、意见或合作意向，欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/60 text-center">
                  <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-mail-line text-xl text-accent-500"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@aimanhua.com</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/60 text-center">
                  <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-wechat-line text-xl text-secondary-500"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">微信公众号</h3>
                  <p className="text-xs text-foreground-500">爱漫画网官方</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/60 text-center">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-chat-smile-2-line text-xl text-primary-500"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">意见反馈</h3>
                  <p className="text-xs text-foreground-500">在网站内提交反馈表单</p>
                </div>
              </div>

              {/* Feedback Form */}
              <ContactForm />
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
}